import { Box, Button, Modal } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTypedSelector } from '@Features/store';
import { useSnackbar } from '@Providers/useSnackbar';
import { CardTargetChip, resolveTargetType } from './CardTarget';
import {
	scratchCardsGetAll,
	scratchCardsUpdate,
} from '../Slices/ScratchCards.slice';
import { ScratchCard } from '../Types/ScratchCards.types';
import {
	CheckboxStyled,
	Container,
	ButtonsContainer,
	TitleStyled,
} from './GenerateCode';
import TargetModePicker, {
	buildTargetPayload,
	GenerationMode,
	modeForCard,
	TARGET_SCHEMA_FIELDS,
} from './TargetModePicker';

type Props = {
	handleModelClose: () => void;
	openModel: boolean;
	classRooms: any;
	/** The (unused) card being retargeted. */
	card: ScratchCard | null;
};

const SCHEMA = yup.object().shape({
	...TARGET_SCHEMA_FIELDS,
	checker: yup.boolean(),
});

/**
 * Retargets one unused card. Full target replacement through the shared mode
 * picker — the backend rejects the edit with a 409 if the card got redeemed
 * between opening the modal and submitting.
 */
const EditCode: React.FC<Props> = ({
	handleModelClose,
	openModel,
	classRooms,
	card,
}) => {
	const dispatch = useDispatch();
	const {
		formState: { isValid, isSubmitting },
		watch,
		reset,
		control,
		handleSubmit,
		setValue,
	} = useForm<any>({
		resolver: yupResolver(SCHEMA),
		mode: 'onTouched',
		defaultValues: {
			checker: false,
			classroom_id: null,
			course_id: null,
			instructor_id: null,
			value: null,
			standalone_course_id: null,
			bundle_id: null,
		},
	});

	const { dateRange, selectedFilters, query, sortBy, meta } = useTypedSelector(
		(state) => state.ScratchCards
	);
	const { displaySnackbar } = useSnackbar();

	const mode: GenerationMode = watch('mode');

	// Prefill from the row every time the modal opens on a card. Raw values are
	// fine: the picker only shows the chosen mode's fields, buildTargetPayload
	// only sends them, and switching mode clears everything anyway.
	useEffect(() => {
		if (!openModel || !card) return;
		reset({
			mode: modeForCard(card),
			classroom_id: card.classroom_id ?? null,
			course_id: card.course_id ?? null,
			standalone_course_id: card.standalone_course_id ?? null,
			bundle_id: card.bundle_id ?? null,
			instructor_id: card.instructor_id ?? null,
			value: card.value ?? null,
			checker: false,
		});
	}, [openModel, card, reset]);

	const onSubmitForm = async (formData: Record<string, any>) => {
		if (!card?.id) return;
		try {
			await (
				dispatch(
					scratchCardsUpdate({
						id: card.id,
						...buildTargetPayload(mode, formData),
					} as any)
				) as any
			).unwrap();
			displaySnackbar('success', 'Scratch card updated successfully');
			dispatch(
				scratchCardsGetAll({
					page: meta?.current_page ?? 1,
					query: query ?? '',
					sortBy,
					filters: selectedFilters,
					perPage: meta?.per_page ?? 10,
					from: dateRange.from,
					to: dateRange.to,
					export: false,
				})
			);
			closeAndReset();
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message ||
					error?.message ||
					'Unable to update scratch card'
			);
		}
	};

	const closeAndReset = () => {
		reset();
		handleModelClose();
	};

	return (
		<Modal
			open={openModel}
			onClose={closeAndReset}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description"
		>
			<Container>
				<form onSubmit={handleSubmit(onSubmitForm)}>
					<Box sx={{ marginLeft: '1.5rem', marginTop: '2.25rem' }}>
						<TitleStyled id="modal-modal-title">
							Edit Code {card ? `(${card.code})` : ''}
						</TitleStyled>
					</Box>

					<Box
						sx={{
							marginLeft: '3.5rem',
							marginRight: '4rem',
							marginTop: '1.5rem',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						{card && (
							<CurrentTargetInfo>
								<span>Current:</span>
								<CardTargetChip target={resolveTargetType(card)} />
								<span>{card.product_label ?? '—'}</span>
								{card.value !== null && card.value !== undefined && (
									<span>· value {card.value}</span>
								)}
							</CurrentTargetInfo>
						)}
						<TargetModePicker
							control={control}
							setValue={setValue}
							watch={watch}
							classRooms={classRooms}
							active={openModel}
						/>

						<Controller
							control={control}
							name="checker"
							render={({ field }) => (
								<CheckboxStyled
									control={<Checkbox />}
									{...field}
									checked={!!field.value}
									label="All selected options are correct"
								/>
							)}
						/>
					</Box>
					<ButtonsContainer>
						<Button
							variant="text"
							color="inherit"
							sx={{
								fontFamily: 'Roboto',
								fontSize: '14px',
								padding: '6px 16px',
								color: '#6c63ff',
								backgroundColor: '#fff',
								outline: 'none',
							}}
							disabled={!isValid || !watch('checker') || isSubmitting}
							type="submit"
						>
							Save
						</Button>
						<Button
							variant="text"
							color="warning"
							sx={{
								fontFamily: 'Roboto',
								fontSize: '14px',
								padding: '6px 16px',
								color: '#ff9800',
								outline: 'none',
								backgroundColor: '#fff',
							}}
							onClick={closeAndReset}
						>
							Cancel
						</Button>
					</ButtonsContainer>
				</form>
			</Container>
		</Modal>
	);
};

export default EditCode;

const CurrentTargetInfo = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.5rem;
	margin-bottom: 1rem;
	font-family: Roboto;
	font-size: 0.875rem;
	color: rgba(0, 0, 0, 0.6);
`;
